/**
 * Virtualized Infinite Scroll — content-swapping windowed scroller for Shopify.
 *
 * Instead of removing DOM nodes (which breaks :nth-child grid layouts and
 * causes scroll jumps), this system keeps every card SHELL in the DOM but
 * clears the innerHTML of off-screen cards ("hollows them out").
 *
 * Why this works
 * ─────────────
 * • Card shells stay in the DOM at their original indices, so :nth-child()
 *   CSS patterns (mosaic featured / hero / compact) are never disrupted.
 * • A hollowed card keeps its measured height via an inline style, so the
 *   scroll position never shifts.
 * • Images are the #1 memory consumer on iOS Safari.  Clearing innerHTML
 *   removes <img> elements, letting WebKit release decoded image buffers.
 * • When a card scrolls back into range, innerHTML is restored from a
 *   lightweight string cache and images re-enter as loading="lazy".
 * • No spacer divs, no height math, no scroll position corrections.
 *
 * Window tracking
 * ──────────────
 *   _activeStart … _activeEnd  — range of cards with live content
 *   Cards outside this range are "hollow" (empty shell + fixed height)
 */
(function () {
  'use strict';

  /* ──────────────────────────── constants ──────────────────────────── */

  var MAX_ACTIVE   = 28;     // max cards with live content
  var BUFFER_PX    = 500;    // fill cards within this distance of viewport
  var PRUNE_PX     = 1400;   // hollow cards beyond this distance
  var PREFETCH_PX  = 1200;   // start fetching next page at this distance
  var TICK_MS      = 200;    // scroll-handler throttle
  var COOLDOWN_MS  = 600;    // min gap between page fetches

  /* ──────────────── iOS: tighter thresholds ───────────────────────── */

  var _isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  if (_isIOS) {
    MAX_ACTIVE  = 22;
    BUFFER_PX   = 400;
    PRUNE_PX    = 1100;
    PREFETCH_PX = 800;
  }

  /* ──────────────────────────── helpers ─────────────────────────────── */

  function stripCarouselInitFlags(html) {
    if (!html) return html;
    return html
      .replace(/\s*data-mosaic-scroll-indicator-bound="true"/gi, '')
      .replace(/\s*data-desktop-wheel-bound="true"/gi, '')
      .replace(/\s*data-mosaic-compact-bound="true"/gi, '')
      .replace(/\s*data-slide-user-control-bound="true"/gi, '');
  }

  function throttle(fn, wait) {
    var last = 0, timer = null;
    return function () {
      var now = Date.now();
      var remaining = wait - (now - last);
      if (remaining <= 0) {
        if (timer) { clearTimeout(timer); timer = null; }
        last = now;
        fn();
      } else if (!timer) {
        timer = setTimeout(function () {
          last = Date.now();
          timer = null;
          fn();
        }, remaining);
      }
    };
  }

  /* ═══════════════════════════════════════════════════════════════════ */
  /*  VirtualizedScroll                                                */
  /* ═══════════════════════════════════════════════════════════════════ */

  class VirtualizedScroll extends HTMLElement {

    connectedCallback() {
      this._destroyed     = false;
      this._isLoading     = false;
      this._fetchedAll    = false;
      this._lastFetchTime = 0;

      this._contentCache  = [];    // innerHTML per card (by DOM index)
      this._isHollow      = [];    // boolean per card
      this._origStyles    = [];    // saved style attr for clean restore
      this._activeStart   = 0;     // first filled card index
      this._activeEnd     = 0;     // one past last filled card index

      this._sectionId      = this.getAttribute('section-id') || '';
      this._targetSelector = this.getAttribute('target') || '';
      this._nextUrl        = this.getAttribute('next-page-url') || '';

      this._target = document.querySelector(this._targetSelector);
      if (!this._target) {
        console.warn('[VirtualizedScroll] target not found:', this._targetSelector);
        this._fallback();
        return;
      }

      this._syncVisibility();
      this._setupSentinel();
      this._seedInitialCards();
      this._bindScroll();
      this._tick();
    }

    disconnectedCallback() {
      this._destroyed = true;
      if (this._scrollHandler) {
        window.removeEventListener('scroll', this._scrollHandler);
      }
    }

    /**
     * External sorters may reorder card shells in the DOM.
     * Virtualized caches are index-based, so we need a full, in-order baseline first.
     */
    prepareForExternalReorder() {
      if (this._destroyed || !this._target) return;
      this._fillAllHollowCards();
      this._reseedCachesFromDom();
    }

    afterExternalReorder() {
      if (this._destroyed || !this._target) return;
      this._reseedCachesFromDom();
      requestAnimationFrame(this._tick.bind(this));
    }

    _syncVisibility() {
      var hasNext = !!this._nextUrl;
      var shouldShow = !this._fetchedAll && hasNext;
      if (shouldShow) this.removeAttribute('hidden');
      else this.setAttribute('hidden', '');
    }

    /* ─────────────── sentinel (for prefetch detection) ─────────────── */

    _setupSentinel() {
      this._sentinel = document.createElement('div');
      this._sentinel.className = 'vs-bottom-sentinel';
      this._sentinel.style.cssText =
        'height:1px;overflow:hidden;pointer-events:none;contain:strict;';
      this._target.after(this._sentinel);
    }

    /* ─────────────── seed page-1 cards ─────────────────────────────── */

    _seedInitialCards() {
      this._reseedCachesFromDom();
    }

    _reseedCachesFromDom() {
      var children = this._target ? this._target.children : [];
      this._contentCache = [];
      this._isHollow = [];
      this._origStyles = [];
      for (var i = 0; i < children.length; i++) {
        this._contentCache.push(stripCarouselInitFlags(children[i].innerHTML));
        this._isHollow.push(false);
        this._origStyles.push(children[i].getAttribute('style') || '');
      }
      this._activeStart = 0;
      this._activeEnd = children.length;
    }

    _fillAllHollowCards() {
      if (!this._target) return;
      for (var i = 0; i < this._target.children.length; i++) {
        if (this._isHollow[i]) this._fillIn(i);
      }
    }

    /* ─────────────── scroll binding ────────────────────────────────── */

    _bindScroll() {
      this._scrollHandler = throttle(this._tick.bind(this), TICK_MS);
      window.addEventListener('scroll', this._scrollHandler, { passive: true });
    }

    /* ═══════════════ main tick ═══════════════════════════════════════ */

    /**
     * Read-then-write cycle to avoid layout thrashing.
     * 1. READ: scan card positions to compute the ideal active window.
     * 2. WRITE: hollow/fill cards in a single batch.
     */
    _tick() {
      if (this._destroyed) return;

      var children = this._target.children;
      var total    = children.length;
      if (total === 0) return;

      var viewH = window.innerHeight;

      /* ── READ PHASE: determine new window bounds ───────────────── */

      var newStart = this._activeStart;
      var newEnd   = this._activeEnd;

      // Expand downward: fill cards approaching viewport from below
      while (newEnd < total) {
        if (children[newEnd].getBoundingClientRect().top > viewH + BUFFER_PX) break;
        newEnd++;
      }

      // Expand upward: fill cards approaching viewport from above
      while (newStart > 0) {
        if (children[newStart - 1].getBoundingClientRect().bottom < -BUFFER_PX) break;
        newStart--;
      }

      // Shrink top: hollow cards that scrolled far above
      while (newStart < newEnd - 1) {
        if (children[newStart].getBoundingClientRect().bottom >= -PRUNE_PX) break;
        newStart++;
      }

      // Shrink bottom: hollow cards that scrolled far below
      while (newEnd > newStart + 1) {
        if (children[newEnd - 1].getBoundingClientRect().top <= viewH + PRUNE_PX) break;
        newEnd--;
      }

      // Enforce MAX_ACTIVE cap by trimming whichever edge is further away
      var viewCenter = viewH / 2;
      while (newEnd - newStart > MAX_ACTIVE) {
        var topCard = children[newStart];
        var botCard = children[newEnd - 1];
        var topDist = Math.abs(topCard.getBoundingClientRect().top - viewCenter);
        var botDist = Math.abs(botCard.getBoundingClientRect().bottom - viewCenter);
        if (topDist >= botDist) {
          newStart++;
        } else {
          newEnd--;
        }
      }

      /* ── WRITE PHASE: apply changes in a single batch ──────────── */

      var changed = false;

      // Hollow cards that left the active window
      var i;
      for (i = this._activeStart; i < Math.min(this._activeEnd, newStart); i++) {
        this._hollowOut(i);
        changed = true;
      }
      for (i = Math.max(this._activeStart, newEnd); i < this._activeEnd; i++) {
        this._hollowOut(i);
        changed = true;
      }

      // Fill cards that entered the active window
      for (i = newStart; i < newEnd; i++) {
        if (this._isHollow[i]) {
          this._fillIn(i);
          changed = true;
        }
      }

      this._activeStart = newStart;
      this._activeEnd   = newEnd;

      // Fetch next page if near bottom
      this._maybeFetch(viewH);

      // If state changed, schedule one more frame to catch any stragglers
      // (handles fast scrolling + scroll-stop scenarios)
      if (changed) {
        var self = this;
        requestAnimationFrame(function () {
          if (!self._destroyed) self._tick();
        });
      }
    }

    /* ═══════════════ hollow out a card (release images) ═════════════ */

    _hollowOut(idx) {
      var card = this._target.children[idx];
      if (!card || this._isHollow[idx]) return;

      // Cache content before clearing (only first time)
      if (this._contentCache[idx] == null) {
        this._contentCache[idx] = stripCarouselInitFlags(card.innerHTML);
      }

      this._origStyles[idx] = card.getAttribute('style') || '';

      // Freeze dimensions so grid layout stays identical
      var h = card.offsetHeight;
      card.style.height    = h + 'px';
      card.style.minHeight = h + 'px';
      card.style.overflow  = 'hidden';

      // Clear inner content — this destroys <img> elements, releasing
      // decoded image buffers (the main memory consumer on iOS Safari)
      card.innerHTML = '';
      this._isHollow[idx] = true;
    }

    /* ═══════════════ fill a card back in from cache ═════════════════ */

    _fillIn(idx) {
      var card = this._target.children[idx];
      if (!card || !this._isHollow[idx]) return;

      var cached = this._contentCache[idx];
      if (cached == null) return;

      // Restore content
      card.innerHTML = cached;

      // Restore original inline styles (remove the height/overflow we added)
      var orig = this._origStyles[idx];
      if (orig) {
        card.setAttribute('style', orig);
      } else {
        card.removeAttribute('style');
      }

      var rect = card.getBoundingClientRect();
      var nearViewport = rect.bottom > -BUFFER_PX && rect.top < window.innerHeight + BUFFER_PX;
      var primaryImg = card.querySelector('.mosaic-card__image, .scrolling-image:first-child img');
      var imgs = card.querySelectorAll('img');
      for (var i = 0; i < imgs.length; i++) {
        var isPrimary = primaryImg && imgs[i] === primaryImg;
        if (nearViewport && isPrimary) {
          imgs[i].setAttribute('loading', 'eager');
          imgs[i].setAttribute('decoding', 'async');
        } else {
          imgs[i].setAttribute('loading', 'lazy');
          imgs[i].removeAttribute('fetchpriority');
        }
      }

      this._isHollow[idx] = false;

      var self = this;
      requestAnimationFrame(function () {
        if (self._destroyed) return;
        var list = card.closest('product-list.collection-mosaic__grid');
        if (list && typeof window.syncMosaicImageSizes === 'function') {
          window.syncMosaicImageSizes(list);
        }
        var mosaicCards = card.querySelectorAll('product-card.mosaic-card');
        for (var m = 0; m < mosaicCards.length; m++) {
          var pc = mosaicCards[m];
          if (typeof pc.initMosaicScrollIndicator === 'function') {
            pc.initMosaicScrollIndicator();
          }
          if (typeof pc.attachScrollingImageCarouselWheel === 'function') {
            pc.attachScrollingImageCarouselWheel();
          }
          if (typeof pc.initMosaicCompactSlideIndex === 'function') {
            pc.initMosaicCompactSlideIndex();
          }
        }
      });
    }

    /* ═══════════════ fetching ═══════════════════════════════════════ */

    _maybeFetch(viewH) {
      if (this._isLoading || this._fetchedAll || !this._nextUrl) return;
      if (Date.now() - this._lastFetchTime < COOLDOWN_MS) return;

      var sentinelRect = this._sentinel.getBoundingClientRect();
      if (sentinelRect.top > viewH + PREFETCH_PX) return;

      this._fetchPage();
    }

    _fetchPage() {
      if (this._isLoading || this._fetchedAll) return;
      this._isLoading = true;
      this._lastFetchTime = Date.now();
      this.removeAttribute('hidden');
      this.setAttribute('aria-busy', 'true');

      var url = this._nextUrl;
      if (this._sectionId) {
        url += (url.indexOf('?') >= 0 ? '&' : '?') + 'section_id=' + this._sectionId;
      }

      var self = this;
      fetch(url)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          self._processFetchedPage(html);
        })
        .catch(function (err) {
          console.error('[VirtualizedScroll] fetch failed:', err);
          self._fetchedAll = true;
          self._fallback();
        })
        .finally(function () {
          self._isLoading = false;
          self.setAttribute('aria-busy', 'false');
          self._syncVisibility();
        });
    }

    _processFetchedPage(responseHtml) {
      var doc = new DOMParser().parseFromString(responseHtml, 'text/html');
      var newList = doc.querySelector(this._targetSelector);

      if (!newList) {
        this._fetchedAll = true;
        this._syncVisibility();
        return;
      }

      var newCards = Array.from(newList.children);
      if (newCards.length === 0) {
        this._fetchedAll = true;
        this._syncVisibility();
        return;
      }

      var fragment = document.createDocumentFragment();
      for (var i = 0; i < newCards.length; i++) {
        var card = newCards[i];

        // Force lazy on all new images
        var imgs = card.querySelectorAll('img');
        for (var j = 0; j < imgs.length; j++) {
          imgs[j].setAttribute('loading', 'lazy');
        }

        // Register in cache as filled
        this._contentCache.push(stripCarouselInitFlags(card.innerHTML));
        this._isHollow.push(false);
        this._origStyles.push(card.getAttribute('style') || '');

        fragment.appendChild(card);
      }

      this._target.appendChild(fragment);
      this._activeEnd = this._target.children.length;

      // Discover next page URL
      var nextEl = doc.querySelector('virtualized-scroll') || doc.querySelector('infinite-scroll');
      if (nextEl && nextEl.getAttribute('next-page-url')) {
        this._nextUrl = nextEl.getAttribute('next-page-url');
        this.setAttribute('next-page-url', this._nextUrl);
      } else {
        this._nextUrl = '';
        this._fetchedAll = true;
        this.removeAttribute('next-page-url');
      }
      this._syncVisibility();

      this._publishAnalytics(newCards);

      if (typeof window.syncMosaicImageSizes === 'function') {
        window.syncMosaicImageSizes(this._target);
      }
      if (typeof window.scheduleApplyMosaicCompactSlides === 'function') {
        window.scheduleApplyMosaicCompactSlides(this._target);
      }

      // Rebalance: hollow distant cards to stay within budget
      requestAnimationFrame(this._tick.bind(this));
    }

    /* ─────────────── analytics ──────────────────────────────────────── */

    _publishAnalytics(cards) {
      try {
        if (typeof Shopify === 'undefined' || !Shopify.analytics) return;
        var items = [];
        for (var i = 0; i < cards.length; i++) {
          var el = cards[i].querySelector('.product-title, .mosaic-card__title');
          var title = el ? el.textContent.trim() : '';
          if (title) items.push({ item_name: title });
        }
        if (items.length) {
          Shopify.analytics.publish('view_item_list', { items: items });
        }
      } catch (e) { /* non-critical */ }
    }

    /* ─────────────── fallback ───────────────────────────────────────── */

    _fallback() {
      this.innerHTML = '';
      if (!this._nextUrl) return;

      var btn = document.createElement('button');
      btn.className = 'button vs-load-more-btn';
      btn.type = 'button';
      btn.textContent = 'Load More Products';
      var self = this;
      btn.addEventListener('click', function () {
        btn.disabled = true;
        btn.textContent = 'Loading\u2026';
        self._isLoading = false;
        self._fetchedAll = false;
        self._fetchPage();
      });
      this.appendChild(btn);
    }
  }

  /* ═══════════════════════════════════════════════════════════════════ */

  if (!window.customElements.get('virtualized-scroll')) {
    window.customElements.define('virtualized-scroll', VirtualizedScroll);
  }

})();
